/**
 * ResumeLessonCard
 * ─────────────────────────────────────────────────────────────
 * Pick-up-where-you-left-off card. Reads the global `ylc_last_lesson`
 * pointer that the InvisibleTeacher writes on every section change and
 * shows a one-tap CTA back into that lesson.
 *
 * Stays hidden unless the saved lesson is still mid-flight (not fully
 * completed) AND the metadata is recent (< 30 days, otherwise stale).
 */

import { useEffect, useState } from "react";

const STORAGE_KEY = "ylc_last_lesson";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readLastLesson() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.lessonTitle) return null;
    if (data.updatedAt && Date.now() - data.updatedAt > MAX_AGE_MS) return null;
    // Treat as already-done if we somehow ended up past the last section
    if (
      typeof data.sectionIndex === "number" &&
      typeof data.totalSections === "number" &&
      data.sectionIndex >= data.totalSections - 1
    ) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function formatLastStudied(updatedAt) {
  if (!updatedAt) return "";
  const mins = Math.floor((Date.now() - updatedAt) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

export default function ResumeLessonCard({ onResume }) {
  const [data, setData] = useState(() => readLastLesson());

  // Re-check on focus and on a storage event in case another tab updates
  useEffect(() => {
    const refresh = () => setData(readLastLesson());
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  if (!data) return null;

  const sectionLabel =
    typeof data.sectionIndex === "number" && typeof data.totalSections === "number"
      ? `Section ${data.sectionIndex + 1} of ${data.totalSections}`
      : null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setData(null);
  };

  const handleResume = () => {
    // Store a "deep-link hint" that the destination component can pick up
    // to skip past intermediate selectors (grade → subject) and jump
    // straight into the saved lesson. The InvisibleTeacher will then
    // restore the saved section from the per-lesson `it_lesson_pos:*` key.
    try { localStorage.setItem("ylc_resume_target", JSON.stringify(data)); } catch {}
    // Fire a custom event so destination sections (SubjectLibrary,
    // UnifiedCourses) can consume the target even if they're already
    // mounted and their initial useEffect has already run. This is the
    // 2026-02 fix for Continue button being non-functional when the
    // student returns from another tab.
    try {
      window.dispatchEvent(new CustomEvent("ylc:resume-target", { detail: data }));
    } catch {}
    onResume && onResume(data);
  };

  return (
    <div
      data-testid="resume-lesson-card"
      onClick={handleResume}
      style={{
        marginBottom: "14px",
        padding: "16px 18px",
        background:
          "linear-gradient(120deg, #1a2440 0%, #1f1a3a 60%, #2a1a3a 100%)",
        border: "1px solid #FFD70060",
        borderRadius: "14px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 10px 32px rgba(255,215,0,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.4)";
      }}
    >
      <div
        style={{
          fontSize: "1.6rem",
          flexShrink: 0,
          width: "44px",
          height: "44px",
          borderRadius: "50%",
          background: "rgba(255,215,0,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        ▶︎
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "0.7rem",
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            color: "#FFD700aa",
            fontWeight: 600,
            marginBottom: "3px",
          }}
        >
          Pick up where you left off
        </div>
        <div
          data-testid="resume-lesson-title"
          style={{
            fontSize: "1rem",
            color: "#fff",
            fontWeight: 600,
            fontFamily: "Georgia, serif",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={data.lessonTitle}
        >
          {data.lessonTitle}
        </div>
        <div
          style={{
            fontSize: "0.78rem",
            color: "#aaa",
            marginTop: "2px",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {data.subject && (
            <span data-testid="resume-lesson-subject">{data.subject}</span>
          )}
          {data.gradeLevel && (
            <>
              {data.subject && <span>·</span>}
              <span data-testid="resume-lesson-grade">Grade {data.gradeLevel}</span>
            </>
          )}
          {sectionLabel && (
            <>
              <span>·</span>
              <span data-testid="resume-lesson-section">{sectionLabel}</span>
            </>
          )}
          {data.updatedAt && (
            <>
              <span>·</span>
              <span style={{ color: "#888" }}>{formatLastStudied(data.updatedAt)}</span>
            </>
          )}
        </div>
      </div>

      <button
        data-testid="resume-lesson-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleResume();
        }}
        style={{
          background: "linear-gradient(90deg, #FFD700, #FFA500)",
          color: "#000",
          border: "none",
          borderRadius: "999px",
          padding: "10px 16px",
          fontSize: "0.85rem",
          fontWeight: "bold",
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          width: "auto",
          marginTop: 0,
        }}
      >
        Continue →
      </button>

      <button
        data-testid="resume-lesson-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="Dismiss"
        style={{
          background: "transparent",
          color: "#888",
          border: "none",
          fontSize: "1.1rem",
          cursor: "pointer",
          padding: "0 4px",
          width: "auto",
          marginTop: 0,
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
}
